import React from "react";
import { OdooTicketsLink, hasOdooTickets } from "@/components/OdooTicketsLink";
import { useParams, Link, useLocation, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Wifi,
  WifiOff,
  Zap,
  Thermometer,
  Radio,
  Settings,
  Send,
  MapPin,
  Building,
  ScrollText,
  Pause,
  Play,
  SlidersHorizontal,
} from "@/components/icons";

import {
  fetchUnit,
  unitHeartbeat,
  permitCharging,
  stopSessionCharging,
  resumeSessionCharging,
  patchUnitSettings,
  fetchUnitBillingTotals,
} from "@/lib/api";
import {
  freshnessNote,
  totalsState,
  UNKNOWN,
  type BillingFigure,
} from "@/lib/billingTotals";
import { BillingFigureDetail } from "@/components/BillingFigureDetail";
import { useAuth } from "@/app/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TouEditor } from "@/views/TouEditor";
import { TariffEditor } from "@/views/TariffEditor";
import { AppliedPricingProfiles } from "@/components/AppliedPricingProfiles";
import { PaymentQrCode } from "@/components/PaymentQrCode";
import { SessionHistory } from "@/views/SessionHistory";
import { UnitAssignment } from "@/views/UnitAssignment";
import { UnitTelemetryPanel } from "@/components/telemetry/UnitTelemetryPanel";
import {Switch} from "@/components/ui/switch";
import {ToggleGroup, ToggleGroupItem} from "@/components/ui/toggle-group";
import { formatEnergy, formatNumber, formatPower, moneyV2Display, type MoneyBasis } from "@voltie/localization-web";
import { useT, type Translate } from "@/app/localization";
import { errorCode, errorMessage } from "@/app/serverErrors";
import type { UnitBillingTotalsRead } from "@/lib/unitBillingTotals";
import { workModeLabel } from "@/lib/unit-labels";

function formatRelativeTime(t: Translate, isoStr: string | null | undefined): string {
  if (!isoStr) return t("operator.unit.last_seen_never");
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return t("operator.unit.last_seen_never");
  const diffMs = Date.now() - d.getTime();
  const diffS = Math.floor(diffMs / 1000);
  // Treat the near-zero window as "just now": this absorbs tiny clock skew
  // between browser and server (small negatives) and very recent pings.
  // Deliberately a SMALL window — a large negative means something is genuinely
  // wrong (it's the symptom of the Task 18 timezone bug) and must stay visible
  // rather than be masked as "just now", so a future regression can't hide.
  if (diffS > -15 && diffS < 15) return t("operator.unit.last_seen_just_now");
  if (diffS < 60) return t("operator.unit.last_seen_ago", { unit: "seconds", amount: String(diffS) });
  const diffM = Math.floor(diffS / 60);
  if (diffM < 60) return t("operator.unit.last_seen_ago", { unit: "minutes", amount: String(diffM) });
  const diffH = Math.floor(diffM / 60);
  if (diffH < 24) return t("operator.unit.last_seen_ago", { unit: "hours", amount: String(diffH) });
  const diffD = Math.floor(diffH / 24);
  return t("operator.unit.last_seen_ago", { unit: "days", amount: String(diffD) });
}

interface BackState {
  backTo?: string;
  /** Catalog key of the calling page's name, translated at render. */
  backLabelKey?: string;
  /** Legacy: an already-translated page name in older history entries. */
  backLabel?: string;
}

/** The calling page's name in the active language; a key this catalog lacks falls back to the legacy text. */
function backPageName(t: Translate, state: BackState | null): string | undefined {
  const key = state?.backLabelKey;
  const name = key ? t(key) : undefined;
  return name && name !== key ? name : state?.backLabel;
}

export function UnitDetailPage() {
  const { unitId } = useParams();
  const id = unitId ? decodeURIComponent(unitId) : "";
  const { isAdmin, isOrgUser } = useAuth();
  const t = useT();
  // Tabs live in the URL so an alert or a ticket can deep-link straight into the
  // telemetry view (with its range and filters). The telemetry panel is mounted
  // ONLY while its tab is open — every query it makes is a paid Athena scan, and
  // opening a charger must cost nothing.
  const [searchParams, setSearchParams] = useSearchParams();
  // Platform admins only — the lake is a support tool, not a tenant view, and
  // every panel it opens is a metered Athena scan. Org managers and drivers do
  // not get the tab, and `?tab=telemetry` in a shared link falls back to the
  // overview for them rather than mounting the panel. The API says the same
  // (web/telemetry_routes.py) — this is the affordance, not the gate.
  const canReadTelemetry = isAdmin;
  const tab =
    canReadTelemetry && searchParams.get("tab") === "telemetry" ? "telemetry" : "overview";
  const navState = useLocation().state as BackState | null;
  const backTo = navState?.backTo ?? "/";
  const backPage = backPageName(t, navState);
  const backLabel = backPage ? t("operator.unit.back_to", { page: backPage }) : t("operator.unit.back");

  /* ---- Queries ---- */
  const unitQ = useQuery({
    queryKey: ["unit", id],
    queryFn: () => fetchUnit(id),
    enabled: !!id,
    refetchInterval: 5_000,
  });

  const billingQ = useQuery({
    queryKey: ["unit", id, "billing", "totals"],
    queryFn: () => fetchUnitBillingTotals(id),
    enabled: !!id && isAdmin,
    refetchInterval: 30_000,
  });
  // A failed refresh keeps the last good totals visible with a note.
  const billing = billingQ.data?.status === "value" ? billingQ.data.value.totals : undefined;
  const billingNote = billing && totalsState(billing) === "ready" ? freshnessNote(t, billing, billingQ.isError) : null;

  /* ---- Mutations ---- */
  const heartbeatM = useMutation({
    mutationFn: () => unitHeartbeat(id),
    onSuccess: () => toast.success(t("operator.unit.heartbeat_sent")),
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.unit.heartbeat_failed" })),
  });

  const permitM = useMutation({
    mutationFn: () => permitCharging(id),
    onSuccess: () => toast.success(t("operator.unit.permit_sent")),
    onError: (e) => toast.error(errorMessage(t, e, { family: "unit_settings", fallback: "operator.unit.permit_failed" })),
  });

  // Stop / Resume the ACTIVE session. The response carries the orchestrator's
  // resulting start_gate — report THAT, never the requested action: a resume re-runs
  // the full permit pipeline, so it can legitimately come back blocked (a TOU window
  // closed, or the driver's card hold expired, while the session was paused).
  const manualStopM = useMutation({
    mutationFn: (stop: boolean) => (stop ? stopSessionCharging(id) : resumeSessionCharging(id)),
    onSuccess: (res, stop) => {
      if (stop) {
        toast.success(t("operator.unit.charging_stopped"));
      } else if (res.permitted) {
        toast.success(t("operator.unit.charging_resumed"));
      } else {
        // The gate code is shown raw: it is the orchestrator's verdict, not a label.
        toast.warning(t("operator.unit.resumed_not_permitted", { gate: res.start_gate }));
      }
      unitQ.refetch();
    },
    onError: (e) => toast.error(errorMessage(t, e, { family: "unit_manual_charging", fallback: "operator.unit.change_charging_failed" })),
  });

  // All single-field settings saves go through PATCH /api/unit/<id>/settings
  // — one route, one cmd:settings_update envelope per save, no cross-stream
  // ordering race (P0-3 fix). Per-field mutations below keep their toast
  // text so the UX is unchanged; only the wire path was consolidated.
  const workModeM = useMutation({
    mutationFn: (mode: "plug-and-charge" | "commercial-public") =>
      patchUnitSettings(id, { work_mode: mode }),
    onSuccess: () => { toast.success(t("operator.unit.work_mode_updated")); unitQ.refetch(); },
    onError: (e) => toast.error(errorMessage(t, e, { family: "unit_settings", fallback: "operator.unit.work_mode_failed" })),
  });

  const selfServiceM = useMutation({
    mutationFn: (enabled: boolean) =>
      patchUnitSettings(id, { customer_self_service_enabled: enabled }),
    onSuccess: () => { toast.success(t("operator.unit.self_service_updated")); unitQ.refetch(); },
    onError: (e) => toast.error(errorMessage(t, e, { family: "unit_settings", fallback: "operator.unit.self_service_failed" })),
  });

  const controlM = useMutation({
    mutationFn: (patch: { name?: string; online_current?: number | null }) =>
      patchUnitSettings(id, {
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(patch.online_current != null
          ? { online_current_limit: patch.online_current }
          : {}),
      }),
    onSuccess: () => { toast.success(t("operator.unit.control_updated")); unitQ.refetch(); },
    onError: (e) => toast.error(errorMessage(t, e, { family: "unit_settings", fallback: "operator.unit.control_failed" })),
  });

  /* ---- Local state ---- */
  const [unitName, setUnitName] = React.useState("");
  const [currentLimit, setCurrentLimit] = React.useState("");

  React.useEffect(() => {
    if (unitQ.data?.name) setUnitName(unitQ.data.name);
    if (unitQ.data?.online_current_limit !== undefined && unitQ.data?.online_current_limit !== null) {
      setCurrentLimit(String(unitQ.data.online_current_limit));
    }
  }, [unitQ.data]);

  const u = unitQ.data;
  // Only false when the server explicitly says the live telemetry mirror is
  // gone (offline > TTL); absent/undefined (legacy responses) counts as OK.
  const telemetryOk = u?.telemetry_available !== false;

  if (unitQ.isLoading) {
    return (
      <div className="flex justify-center p-12" role="status">
        <span className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
        <span className="sr-only">{t("common.state.loading")}</span>
      </div>
    );
  }

  if (unitQ.isError) {
    return (
      <div className="p-5">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">{t("operator.unit.load_failed")}</div>
      </div>
    );
  }

  const isCharging = (u?.state_name || "").toLowerCase() === "charging";
  const isOnline = u?.is_online ?? false;

  // ── Manual stop/resume ──────────────────────────────────────────────────────
  // Pure projection of backend state: whether a session is live, and what the PDP's
  // gate verdict currently is. The page never infers charging permission —
  // `stoppedByUser` is read from the gate, never from work_mode, tariff, current or
  // local optimistic state. Any work mode may be paused (paid/public included); the
  // orchestrator refuses what it cannot serve and the response says why.
  const session = u?.current_session;
  const sessionActive =
    !!session?.session_id &&
    session?.is_active !== false &&
    String(session?.session_ended ?? "") !== "1";
  const stoppedByUser = session?.start_gate === "stopped_by_user";
  const canManualStop = sessionActive;

  return (
    <div className="space-y-5 pt-[86px]">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" className="gap-1 mb-2" asChild>
            <Link to={backTo}><ArrowLeft className="h-4 w-4" /> {backLabel}</Link>
          </Button>
          <h1 className="text-2xl font-semibold">{u?.name || id}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs opacity-60">{id}</span>
            {isOnline ? (
              <Badge variant="success"><Wifi className="h-3 w-3 mr-1" /> {t("common.status.online")}</Badge>
            ) : (
              <Badge variant="destructive"><WifiOff className="h-3 w-3 mr-1" /> {t("common.status.offline")}</Badge>
            )}
            {u?.state_name && <Badge variant={isCharging ? "success" : "default"}>{u.state_name}</Badge>}
            {/* The session's gate verdict, projected. Shown whenever the PDP says
                the session is stopped, so the operator can tell "paused by us"
                apart from "the car finished". */}
            {stoppedByUser && (
              <Badge variant="destructive" className="gap-1">
                <Pause className="h-3 w-3" /> {t("operator.unit.charging_stopped")}
              </Badge>
            )}
            {u?.work_mode === "commercial-public" && (
              <span className="inline-flex items-center rounded-full bg-indigo-100 text-indigo-700 px-2.5 py-0.5 text-xs font-medium">{workModeLabel(t, "commercial-public")}</span>
            )}
            {u?.organization && (
              // Clickable for roles that can open /organizations/:orgId
              // (RequireAdminOrOrg); plain badge for everyone else.
              (isAdmin || isOrgUser) ? (
                <Link to={`/organizations/${u.organization.id}`} title={t("operator.unit.open_organization")}>
                  <Badge variant="default" className="gap-1 cursor-pointer hover:opacity-80">
                    <Building className="h-3 w-3" /> {u.organization.name}
                  </Badge>
                </Link>
              ) : (
                <Badge variant="default" className="gap-1"><Building className="h-3 w-3" /> {u.organization.name}</Badge>
              )
            )}
            {u?.load_balancing_group && (
              // Clickable for roles that can open /load-balancing (RequireAdminOrOrg);
              // deep-links to this group's card via ?group=<id>. Plain badge otherwise.
              (isAdmin || isOrgUser) ? (
                <Link
                  to={`/load-balancing?group=${encodeURIComponent(u.load_balancing_group.group_id)}`}
                  title={t("operator.unit.open_lb_group")}
                >
                  <Badge variant="info" className="gap-1 cursor-pointer hover:opacity-80">
                    <SlidersHorizontal className="h-3 w-3" /> {u.load_balancing_group.name || u.load_balancing_group.group_id}
                  </Badge>
                </Link>
              ) : (
                <Badge variant="info" className="gap-1">
                  <SlidersHorizontal className="h-3 w-3" /> {u.load_balancing_group.name || u.load_balancing_group.group_id}
                </Badge>
              )
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Message-log stream for THIS unit (the Logs page, pre-filtered) —
              admin-gated because /logs is RequireAdmin. */}
          {isAdmin && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/logs?unit_id=${encodeURIComponent(id ?? "")}`}>
                <ScrollText className="h-4 w-4 mr-1" /> {t("operator.nav.logs")}
              </Link>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => heartbeatM.mutate()} disabled={heartbeatM.isPending}>
            <Send className="h-4 w-4 mr-1" /> {t("operator.unit.heartbeat")}
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={() => permitM.mutate()} disabled={permitM.isPending}>
              <Zap className="h-4 w-4 mr-1" /> {t("operator.unit.permit")}
            </Button>
          )}
          {/* Stop / Resume the active session — only while one is live, since there
              is nothing to pause otherwise. The 0 A comes from the orchestrator's
              gate verdict, never from this button. */}
          {canManualStop && (
            stoppedByUser ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => manualStopM.mutate(false)}
                disabled={manualStopM.isPending}
              >
                <Play className="h-4 w-4 mr-1" /> {t("operator.unit.resume")}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => manualStopM.mutate(true)}
                disabled={manualStopM.isPending}
              >
                <Pause className="h-4 w-4 mr-1" /> {t("operator.unit.stop")}
              </Button>
            )
          )}
        </div>
      </div>

      {/* Tabs. A lone "Overview" tab is not a choice, so non-admins get no
          tab bar at all rather than a one-item one. */}
      {canReadTelemetry && (
      <div className="flex gap-1 border-b border-border" role="tablist">
        {([
          { id: "overview", label: t("operator.unit.tab_overview") },
          { id: "telemetry", label: t("operator.unit.tab_telemetry") },
        ] as const).map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            data-testid={`unit-tab-${item.id}`}
            onClick={() =>
              setSearchParams(
                (current) => {
                  const next = new URLSearchParams(current);
                  if (item.id === "overview") next.delete("tab");
                  else next.set("tab", item.id);
                  return next;
                },
                { replace: true },
              )
            }
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
              tab === item.id
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>
      )}

      {tab === "telemetry" ? (
        <UnitTelemetryPanel unitId={id} />
      ) : (
      <>
      {/* Support tickets and warranty in VoltiE's Odoo: admins (the API decides the same) */}
      {u && isAdmin && hasOdooTickets(id) && <OdooTicketsLink unitId={id} />}

      {/* Location */}
      {(u?.station_name || u?.station_address || u?.location) && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {t("operator.unit.location")}</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                {u.station_name && (
                  <div className="text-base font-semibold">{u.station_name}</div>
                )}
                {u.station_address && (
                  <div className="text-sm opacity-70 mt-0.5">{u.station_address}</div>
                )}
                {!u.station_name && !u.station_address && u.location && (
                  <div className="text-sm opacity-70">{u.location}</div>
                )}
                {u.station_time_zone && (
                  <div className="text-xs opacity-50 mt-1">{t("operator.unit.station_zone", { timezone: u.station_time_zone })}</div>
                )}
              </div>
              {u.station_latitude != null && u.station_longitude != null && (
                <div>
                  <div className="text-xs opacity-60">{t("operator.unit.coordinates")}</div>
                  <div className="font-mono text-sm mt-1">
                    {u.station_latitude.toFixed(6)}, {u.station_longitude.toFixed(6)}
                  </div>
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${u.station_latitude}&mlon=${u.station_longitude}&zoom=18`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-primary hover:underline mt-1 inline-block"
                  >
                    {t("operator.unit.view_on_map")}
                  </a>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Live Telemetry */}
      <Card>
        <CardHeader><CardTitle>{t("operator.unit.live_telemetry")}</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3">
            {/* telemetry_available=false → the telemetry mirror expired (unit
                offline): the numeric fields are placeholder zeros, not real
                readings — render "—" instead of a misleading 0.0 V. */}
            <TelemetryItem label={t("operator.metric.voltage")} value={telemetryOk ? u?.voltage : null} unit="V" decimals={1} />
            <TelemetryItem label={t("operator.metric.current")} value={telemetryOk ? u?.current : null} unit="A" decimals={1} />
            <TelemetryItem label={t("operator.metric.power")} value={telemetryOk ? u?.power_kw : null} format={(v) => formatPower(v, "kW", "precise")} />
            <TelemetryItem label={t("operator.metric.frequency")} value={telemetryOk ? u?.frequency : null} unit="Hz" decimals={1} />
            <TelemetryItem label={t("operator.metric.power_factor")} value={telemetryOk ? u?.power_factor : null} unit="" decimals={2} />
            <TelemetryItem label={t("operator.metric.session_energy")} value={telemetryOk && u?.session_energy ? u.session_energy : null} format={(wh) => formatEnergy(wh, "Wh", "summary")} icon={<Zap className="h-4 w-4 text-success" />} />
            <TelemetryItem label={t("operator.metric.lifetime_energy")} value={telemetryOk && u?.lifetime_energy ? u.lifetime_energy / 1000 : null} unit="kWh" decimals={1} icon={<Zap className="h-4 w-4 text-info" />} />
            <TelemetryItem label={t("operator.metric.temperature")} value={telemetryOk ? u?.temperature : null} unit="°C" decimals={0} icon={<Thermometer className="h-4 w-4 text-warning" />} />
            <TelemetryItem label={t("operator.metric.wifi_rssi")} value={telemetryOk ? u?.wifi_rssi : null} unit="dBm" decimals={0} icon={<Radio className="h-4 w-4 text-info" />} />
            <div className="rounded-xl border border-border p-3">
              <div className="text-xs opacity-60">{t("operator.metric.ip")}</div>
              <div className="mt-1 font-mono text-xs">{u?.ip_address || "—"}</div>
              {u?.location && <div className="mt-0.5 text-xs opacity-50 truncate">{u.location}</div>}
              {u?.location_isp && <div className="mt-0.5 text-xs opacity-40 truncate">{u.location_isp}</div>}
            </div>
            <div className="rounded-xl border border-border p-3">
              <div className="text-xs opacity-60">{t("operator.metric.mac")}</div>
              <div className="mt-1 font-mono text-xs">{u?.mac_address || "—"}</div>
            </div>
            <div className="rounded-xl border border-base-200 p-3">
              <div className="text-xs opacity-60">{t("operator.metric.last_seen")}</div>
              <div className="mt-1 font-semibold text-sm">{formatRelativeTime(t, u?.last_seen)}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Work Mode + Tariff (admin or org member) */}
        {(isAdmin || isOrgUser) && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Settings className="h-4 w-4" /> {t("operator.unit.work_mode")}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <ToggleGroup
              type="single"
              size="sm"
              variant="outline"
              value={u?.work_mode ?? "plug-and-charge"}
              onValueChange={(v) => v && workModeM.mutate(v as "plug-and-charge" | "commercial-public")}
            >
              <ToggleGroupItem value="plug-and-charge" disabled={workModeM.isPending}>{workModeLabel(t, "plug-and-charge")}</ToggleGroupItem>
              <ToggleGroupItem value="commercial-public" disabled={workModeM.isPending}>{workModeLabel(t, "commercial-public")}</ToggleGroupItem>
            </ToggleGroup>
            {u?.work_mode === "commercial-public" && (
              <>
                <p className="text-xs text-muted-foreground -mb-2">
                  <strong>{t("operator.unit.default_rate")}</strong> {t("operator.unit.default_rate_hint")}
                </p>
                <TariffEditor unitId={id} onSuccess={() => unitQ.refetch()} inline />
                <AppliedPricingProfiles unitId={id} />
                <div className="border-t pt-6 mt-2">
                  <div className="flex items-center justify-between gap-4">
                    <Label>{t("operator.unit.self_service")}</Label>
                    <Switch
                      checked={u?.customer_self_service_enabled ?? true}
                      onCheckedChange={(val) => selfServiceM.mutate(val)}
                      disabled={selfServiceM.isPending}
                    />
                  </div>
                  {(u?.customer_self_service_enabled ?? true) && (
                    <>
                      <div className="mt-2 text-xs opacity-60">
                        {t("operator.unit.self_service_url", { url: `${window.location.origin}/charge/${encodeURIComponent(id)}` })}
                      </div>
                      <PaymentQrCode
                        url={`${window.location.origin}/charge/${encodeURIComponent(id)}`}
                        unitName={u?.name || id}
                      />
                    </>
                  )}
                  {!(u?.customer_self_service_enabled ?? true) && (
                    <div className="mt-2 text-xs opacity-60">
                      {t("operator.unit.self_service_disabled")}
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
        )}

        {/* Unit Control */}
        <Card>
          <CardHeader><CardTitle>{t("operator.unit.control_title")}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <Label>{t("operator.unit.name_label")}</Label>
                {u?.config_available === false ? (
                  <div className="mt-1 text-sm">
                    <span className="text-red-600 font-medium">{t("operator.unit.editing_disabled")}</span>{" "}
                    <span className="text-xs opacity-50">
                      {t("operator.unit.editing_disabled_reason")}
                    </span>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-1">
                    <Input value={unitName} onChange={(e) => setUnitName(e.target.value)} />
                    <Button
                      variant="outline"
                      onClick={() => controlM.mutate({ name: unitName })}
                      disabled={controlM.isPending}
                    >
                      {t("common.action.save")}
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <Label>{t("operator.unit.online_limit")}</Label>
                {u?.config_available === false ? (
                  <div className="mt-1 text-sm">
                    <span className="text-red-600 font-medium">{t("operator.unit.no_data")}</span>{" "}
                    <span className="text-xs opacity-50">
                      {t("operator.unit.no_data_reason")}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="number"
                        min={6}
                        max={u?.unit_rating ?? undefined}
                        value={currentLimit}
                        onChange={(e) => setCurrentLimit(e.target.value)}
                      />
                      <Button
                        variant="outline"
                        onClick={() => controlM.mutate({ online_current: parseFloat(currentLimit) || null })}
                        disabled={controlM.isPending}
                      >
                        {t("common.action.save")}
                      </Button>
                    </div>
                    {u?.unit_rating && (
                      <p className="text-xs text-primary mt-1">{t("operator.unit.limit_range", { max: String(u.unit_rating) })}</p>
                    )}
                  </>
                )}
              </div>
              {u?.tou_enabled && u?.effective_current_limit !== undefined && u.effective_current_limit !== null
                && u.effective_current_limit < (u?.online_current_limit ?? Infinity) && (
                <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                  {u.effective_current_limit === 0
                    ? t("operator.unit.tou_blocking")
                    : t("operator.unit.tou_limiting", { effective: String(u.effective_current_limit), setting: String(u?.online_current_limit) })}
                </div>
              )}
              {isAdmin && u?.config_available !== false && u?.offline_current_limit != null && (
                <div>
                  <Label>{t("operator.unit.offline_limit")}</Label>
                  <div className="mt-1 text-sm font-mono opacity-70">{u.offline_current_limit}A <span className="text-xs opacity-50">{t("operator.unit.internal_parameter")}</span></div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* TOU (includes the ToU timezone, which anchors the schedule windows) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TouEditor
          key={id}
          unitId={id}
          touEnabled={(u as any)?.tou_enabled ?? false}
          touSchedules={(u as any)?.tou_schedules ?? []}
          timezone={u?.timezone ?? ""}
          configUnavailable={u?.config_available === false}
          onSuccess={() => unitQ.refetch()}
        />
      </div>

      {/* Sessions */}
      <SessionHistory unitId={id} showCosts={isAdmin || u?.work_mode === "commercial-public"} />

      {/* Billing (admin only) */}
      {isAdmin && (
        <Card>
          <CardHeader><CardTitle>{t("operator.unit.billing_title")}</CardTitle></CardHeader>
          <CardContent>
            <UnitBillingTotalsBody t={t} query={billingQ} note={billingNote} />
          </CardContent>
        </Card>
      )}

      {/* Assigned Users (admin only) */}
      {isAdmin && <UnitAssignment unitId={id} />}
      </>
      )}
    </div>
  );
}

/* ---- Billing totals ---- */
const BASIS_LABEL: Record<MoneyBasis, string> = {
  billed: "operator.pricing.billing_finalized",
  estimate: "operator.unit.active_estimated_revenue",
  captured: "operator.unit.totals_basis_captured",
};

const EXCLUSION_REASONS = [
  ["unresolved_billing_rows", "unresolved_billing"],
  ["conflict_rows", "conflict"],
  ["invalid_rows", "invalid"],
  ["unknown_currency_rows", "unknown_currency"],
] as const;

/**
 * The cached totals of the unit: master's cache states (loading, calculating, unavailable,
 * freshness) and one tile per server money group in server order. Amounts come from the
 * money_v2 groups only; groups of different currencies or bases are never added.
 */
function UnitBillingTotalsBody({
  t,
  query,
  note,
}: {
  t: Translate;
  query: UseQueryResult<UnitBillingTotalsRead>;
  note: string | null;
}) {
  if (query.isLoading) return <div className="text-sm opacity-60">{t("common.state.loading")}</div>;
  const read = query.data;
  if (!read) {
    const incompatible = errorCode(query.error) === "money_contract_incompatible";
    return (
      <div className="text-sm text-error">
        {t(incompatible ? "operator.unit.totals_money_incompatible" : "operator.unit.billing_load_failed")}
      </div>
    );
  }
  if (read.status !== "value") return <div className="text-sm text-error">{t("common.state.unavailable")}</div>;

  const { totals, money } = read.value;
  const state = totalsState(totals);
  if (state === "calculating") {
    return <div className="text-sm opacity-60" role="status">{t("operator.pricing.billing_calculating")}</div>;
  }
  if (state === "unavailable") return <div className="text-sm text-error">{t("operator.pricing.billing_unavailable")}</div>;

  const { groups, completeness } = money;
  const estimateUnavailable = totals.active_estimate_status === "unavailable" && !groups.some((g) => g.basis === "estimate");
  const excluded = EXCLUSION_REASONS.filter(([counter]) => completeness[counter] > 0);
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {groups.length === 0 && (
          <div className="rounded-xl border border-border p-3 text-sm opacity-60">{t("operator.unit.totals_empty")}</div>
        )}
        {groups.map((g) => (
          <BillingTile
            key={`${g.basis}/${g.money.currency}`}
            label={t(BASIS_LABEL[g.basis])}
            figure={moneyV2Display(g.money) ?? t("common.state.unavailable")}
          />
        ))}
        {estimateUnavailable && (
          <BillingTile
            label={t("operator.unit.active_estimated_revenue")}
            figure={UNKNOWN}
            title={t("operator.unit.active_estimate_unavailable")}
          />
        )}
        <BillingTile
          label={t("operator.unit.total_sessions")}
          figure={totals.total_sessions == null ? UNKNOWN : formatNumber(totals.total_sessions)}
        />
        <BillingTile
          label={t("operator.metric.total_energy")}
          figure={totals.total_energy_kwh == null ? UNKNOWN : formatEnergy(totals.total_energy_kwh, "kWh", "summary")}
        />
      </div>
      {excluded.length > 0 && (
        <div className="text-sm text-warning">
          <div>
            {t("operator.unit.totals_incomplete", {
              count: excluded.reduce((sum, [counter]) => sum + completeness[counter], 0),
            })}
          </div>
          <ul className="list-disc pl-5">
            {excluded.map(([counter, reason]) => (
              <li key={reason}>{t("operator.unit.totals_excluded_reason", { reason, count: completeness[counter] })}</li>
            ))}
          </ul>
        </div>
      )}
      {note && <div className="text-xs opacity-60" data-testid="billing-freshness">{note}</div>}
    </div>
  );
}

function BillingTile({ label, figure, title }: { label: string; figure: BillingFigure | string; title?: string }) {
  return (
    <div className="rounded-xl border border-border p-3">
      <div className="text-xs opacity-60">{label}</div>
      {typeof figure === "string" ? (
        <div className="text-xl font-semibold" title={title}>{figure}</div>
      ) : (
        <>
          <div className="text-xl font-semibold" title={title}>{figure.value}</div>
          <BillingFigureDetail figure={figure} />
        </>
      )}
    </div>
  );
}

/* ---- Telemetry Item ---- */
function TelemetryItem({
  label,
  value,
  unit = "",
  decimals = 1,
  format,
  icon,
}: {
  label: string;
  value: number | null | undefined;
  unit?: string;
  decimals?: number;
  /** Shared formatter for values with a unit-aware preset (power, energy). */
  format?: (value: number) => string;
  icon?: React.ReactNode;
}) {
  const display = value !== null && value !== undefined && isFinite(value)
    ? format
      ? format(value)
      : `${formatNumber(value, decimals)} ${unit}`.trim()
    : "—";

  return (
    <div className="rounded-xl border border-border p-3">
      <div className="flex items-center gap-1 text-xs opacity-60">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-semibold">{display}</div>
    </div>
  );
}
